// src/pages/dashboard/branch/settings/RestaurantSettingsPage.tsx
import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	FiClock,
	FiLayout,
	FiRefreshCw,
	FiSave,
	FiType,
	FiUser,
	FiSettings,
	FiPackage,
	FiDollarSign
} from 'react-icons/fi';
import { PiPaintBrushBroadBold } from "react-icons/pi";
import { useParams } from 'react-router-dom';

import Badge from '../../../../components/ui/Badge';
import Button from '../../../../components/ui/Button';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { selectBranchLoading, selectCurrentBranch, selectCurrentOrganization } from '../../../../store/selectors';
import { fetchBranchById, setHasChanges as setBranchHasChanges, updateBranchData } from '../../../../store/slices/branchSlice';
import { RestaurantTheme } from '../../../../types/restaurant';
import { LocationType, ThemeType } from '../../../../types/custom';
import { Branch } from '../../../../utils/supabaseClient';
// import BrandSettings from './componenets/BrandSettings';
import LayoutSettings from './componenets/LayoutSettings';
import OperatingHoursSettings from './componenets/OperatingHoursSettings';
import Preview from './componenets/Preview';
import ThemeSettings from './componenets/ThemeSettings';
import TypographySettings from './componenets/TypographySettings';
import { defaultTheme } from '../../../../utils/defaultValues';
import BrandSettings from './componenets/BrandSettings';
import ServicesSettings from './componenets/ServicesSettings';
import FeaturesSettings from './componenets/FeaturesSettings';
import IngredientSettings from './componenets/IngredientSettings';
import CurrenciesSettings from './currencies/CurrenciesSettings';



// Settings tabs configuration
const settingsTabs = [
	{ id: 'brand', label: 'settings.tabs.brand', icon: FiUser },
	{ id: 'theme', label: 'settings.tabs.theme', icon: PiPaintBrushBroadBold },
	{ id: 'layout', label: 'settings.tabs.layout', icon: FiLayout },
	{ id: 'typography', label: 'settings.tabs.typography', icon: FiType },
	{ id: 'hours', label: 'settings.tabs.hours', icon: FiClock },
	{ id: 'services', label: 'settings.tabs.services', icon: FiSettings },
	{ id: 'features', label: 'settings.tabs.features', icon: FiPackage },
	{ id: 'currency', label: 'settings.tabs.currency', icon: FiDollarSign },
];

const RestaurantSettingsPage: React.FC = () => {
	const { t, i18n } = useTranslation();
	const { branchId } = useParams<{ branchId: string }>();
	const isRTL = i18n.language === 'ar';

	const [activeTab, setActiveTab] = useState('brand');
	const [isSaving, setIsSaving] = useState(false);
	const [hasChanges, setHasChanges] = useState(false);

	// Restaurant settings state
	// const [defaulValues, setDefaultValues] = useState<Branch | null>(null);
	const [restaurant, setRestaurant] = useState<Branch | null>(null);

	const branch = useAppSelector(selectCurrentBranch)
	// const isLoadingBranch = useAppSelector(selectBranchLoading)
	// const branchHasChanges = useAppSelector(selectHasChanges)

	// const organization = useAppSelector(selectCurrentOrganization)
	const dispatch = useAppDispatch()

	useEffect(() => {
		// const fetchBranchData = async () => {
		// 	const data = await getBranchById(branchId!)
		// 	setDefaultValues(data)
		// 	setRestaurant(data)
		// }
		// fetchBranchData();

		// if (branchId && !isLoadingBranch && !branch && organization) {
		// 	dispatch(fetchBranchById({branchId, organizationId: organization.id}))
		// }
	}, [branchId])

	useEffect(() => {
		if (branch && !restaurant) {
			setRestaurant(branch)
		}
	}, [branch])

	useEffect(() => {
		if (restaurant && !restaurant.theme) {
			updateTheme(defaultTheme);
		}
	}, [restaurant])

	// Update restaurant settings
	const updateRestaurant = (updates: Partial<Branch>) => {
		setRestaurant(prev => prev
			? {
				...prev,
				...updates,
				location: prev.location // preserve location structure
			}
			: prev
		);
		setHasChanges(true);
		dispatch(setBranchHasChanges(true));
	};

	const updateLocation = (newLocation: Partial<{ google_maps_link: string, coordinates: string, place_id: string }>) => {
		setRestaurant(prev => {
			if (!prev) return prev;
			const updatedLocation = { ...(prev?.location as LocationType || {}), ...newLocation };
			return {
				...prev,
				location: updatedLocation
			};
		});
		setHasChanges(true);
		dispatch(setBranchHasChanges(true));
	}

	const updateTheme = (updates: Partial<RestaurantTheme>) => {
		setRestaurant(prev => ({
			...prev!,
			theme: { ...(prev!.theme as ThemeType || {}), ...updates }
		}));
		setHasChanges(true);
		dispatch(setBranchHasChanges(true));
	};

	// Save settings
	const handleSave = async () => {
		setIsSaving(true);
		try {
			// Here you would make an API call to save the settings
			// await new Promise(resolve => setTimeout(resolve, 1000));

			const dataToUpdate = {
				id: branchId!,
				name: restaurant?.name,
				description: restaurant?.description,
				phone: restaurant?.phone,
				email: restaurant?.email,
				address: restaurant?.address,
				social_media: restaurant?.social_media,
				operating_hours: restaurant?.operating_hours,
				arrival_time : restaurant?.arrival_time,
				theme: restaurant?.theme,
				logo: restaurant?.logo,
				cover_image: restaurant?.cover_image,
				default_item_image: restaurant?.default_item_image,
				location: restaurant?.location,
				ar: restaurant?.ar,
				languages: restaurant?.languages,
				is_setup: true
			}
			// const result = await updateBranch(dataToUpdate);
			// if (!result) return;
			// if (!result.success) console.log(result.message);

			await dispatch(updateBranchData(dataToUpdate))

			setHasChanges(false);
			dispatch(setBranchHasChanges(true));
		} catch (error) {
			console.error('Error saving settings:', error);
		} finally {
			setIsSaving(false);
		}
	};

	// Reset to defaults
	const handleReset = () => {
		setRestaurant(branch);
		setHasChanges(false);
		dispatch(setBranchHasChanges(false));
	};

	// Render settings content based on active tab
	const renderSettingsContent = () => {
		const props = { restaurant, updateRestaurant, updateTheme, branchId, updateLocation }
		switch (activeTab) {
			case 'brand':
				return <BrandSettings {...props} />;
			case 'theme':
				return <ThemeSettings {...props} />;
			case 'layout':
				return <LayoutSettings {...props} />;
			case 'typography':
				return <TypographySettings {...props} />;
			case 'hours':
				return <OperatingHoursSettings {...props} />;
			case 'services':
				return <ServicesSettings {...props} />;
			case 'features':
				return <FeaturesSettings restaurant={restaurant} updateTheme={updateTheme} updateRestaurant={updateRestaurant} />;
			case 'currency':
				return <CurrenciesSettings />;
			default:
				return <ThemeSettings {...props} />;
		}
	};

	return (
		<div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
			<div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
				<div>
					<h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
						{t('settings.title')}
					</h1>
					<p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
						{t('settings.subtitle')}
					</p>
				</div>

				<div className="flex flex-col sm:flex-row sm:items-center gap-3">
					{hasChanges && (
						<Badge variant="warning" className="animate-pulse">
							{t('settings.unsavedChanges')}
						</Badge>
					)}
					<Button
						variant="outline"
						onClick={handleReset}
						leftIcon={<FiRefreshCw />}
					>
						{t('settings.resetToDefaults')}
					</Button>
					<Button
						onClick={handleSave}
						isLoading={isSaving}
						leftIcon={<FiSave />}
					>
						{t('settings.saveChanges')}
					</Button>
				</div>
			</div>

			{/* Tabs Navigation */}
			<div className="mb-8">
				<div className="border-b border-gray-200 dark:border-gray-700">
					<nav className="-mb-px flex space-x-8 overflow-x-auto">
						{settingsTabs.map((tab) => {
							const Icon = tab.icon;
							return (
								<button
									key={tab.id}
									onClick={() => setActiveTab(tab.id)}
									className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm cursor-pointer flex items-center gap-2 ${activeTab === tab.id
										? 'border-primary text-primary'
										: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
										}`}
								>
									<Icon className="h-5 w-5" />
									<span>{t(tab.label)}</span>
								</button>
							);
						})}
					</nav>
				</div>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
				{/* Settings Content */}
				<div className="lg:col-span-3">
					<motion.div
						key={activeTab}
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.3 }}
					>
						{renderSettingsContent()}
					</motion.div>
				</div>

				{/* Live Preview */}
				<div className="lg:col-span-2">
					<Preview restaurant={restaurant} />
				</div>
			</div>
		</div>
	);
};

export default RestaurantSettingsPage;